import clsx from "clsx";
import { Plus } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { basisLabel, fraction, fromNet, rateFor, toCents, toNet, toNumber } from "../../shared/money";
import { HoldButton, useConfirm } from "../../web/components/Confirm";
import { ChangeLines } from "./ChangeLines";
import { draftPrice, type ItemDraft, itemAsDrafted } from "./itemDrafts";
import { ProfitBox } from "./ProfitBox";
import { PartsPriceCard, useMakePlain } from "./PackageCards";
import { LabourSharesCard, LabourTimeCard, labourUnavailable, startingLabour } from "./LabourTimeCard";

export type { ItemDraft } from "./itemDrafts";
import { FixHere } from "../../web/components/FixHere";
import { alreadyShown, markShown } from "../../web/lib/flash";
import { SelectField, useDraftInvalid } from "../../web/components/fields";
import { DraftBar, ErrorNotice, Notice, SectionCard, SectionHeading } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { can, type CostDraft, freeGroupName, groupOf, groupTaxBasis, itemName, itemTax, memberOf, membersOf, quoteRate, usePriceListSaver } from "./data";
import { PricingEditor } from "./PricingEditor";
import { quoteKey } from "./QuotedAmount";
import { describeGroup } from "./describe";
import { GroupCardHeader } from "./GroupCardHeader";
import { isProduct, type MaterialRow, type PriceListData, type PriceMode, type PricingRule, GROUP_KIND_LABEL, type GroupRow, groupKind, type TaxQuote, usesGroup } from "./meta";
import { splitName } from "./names";
import { usePriceListWindows } from "./windows";
import { baseForPrice, type Basis, exactMarkup, isPlain, itemProfit, keepShownAmounts, needsCheck, percentFor, samePrice, sameRuleShown, shownAmount, storedAmount } from "./pricing";
import { plural } from "../../shared/words";

/*
 * An item's cost, tax and price. The price is fixed (typed in), or a markup on the cost (PricingEditor.tsx). Pricing amounts are shown and typed in the item's
 * basis (with or without its tax), and stored before tax (see pricing.ts); the cost is typed as it's
 * quoted (QuotedAmount.tsx: "ex GST" until changed), or shown as the supplier price it follows is. Below: what it makes per
 * unit, before tax (ProfitBox).
 * Using a group, the pricing is the group's, so it's locked here: it's changed in the group's window (every item's new
 * price shown before it's saved), or the item gets its own price (Own price: it leaves the group). So is how it's taxed, with
 * a fixed price (the group says that too): on its own, the tax is chosen with its pricing.
 */

/** Just the rule of an item's pricing (a group's with its tiers). */
const ruleOnly = (p: PricingRule): PricingRule => ({ mode: p.mode, markupValue: p.markupValue, basePrice: p.basePrice, tiers: p.tiers ?? null });

/** An item's Price card (as its Cost card, "What one costs you"): a group's is its Pricing, which needn't be one price. */
const SELLS_FOR = "What one sells for";

/** An item's own pricing as saved, as a draft (what an edit is compared with). */
function savedItemDraft(data: PriceListData, item: MaterialRow): ItemDraft {
    const pricing = data.itemPricing[item.uuid];
    return {
        rule: pricing && !usesGroup(pricing) ? ruleOnly(pricing) : null,
        price: toCents(toNumber(item.price)),
        tax: { rateUuid: itemTax(data, item).uuid, includesTax: item.price_includes_taxes },
    };
}

/** A typed price (as typed, in `basis`) as the plain rule it stands for: a fixed price, nothing else. */
export const typedPriceRule = (price: string, basis: Basis): PricingRule => ({
    mode: "manual",
    markupValue: "0",
    basePrice: storedAmount(price, basis),
});

/** A tax's basis, for comparing amounts as they're shown. */
export const basisOfTax = (tax: TaxQuote, rates: PriceListData["taxRates"]): Basis => ({ includesTax: tax.includesTax, tax: fraction(rateFor(tax.rateUuid, rates)) });

/** Whether two drafts read the same on screen (pricing.ts sameRuleShown): an edit undone is no edit. A typed price is compared as the base price it is. */
function sameItemDraft(a: ItemDraft, b: ItemDraft, rates: PriceListData["taxRates"]) {
    if (quoteKey(a.tax, rates) !== quoteKey(b.tax, rates)) return false;
    const basis = basisOfTax(a.tax, rates);
    const ruleOf = (d: ItemDraft): PricingRule =>
        d.rule ?? typedPriceRule(d.price, basis);
    return sameRuleShown(ruleOf(a), ruleOf(b), basis);
}

/**
 * An item's pricing (its cost shown, set on its Costing tab): a draft while it's edited, like a
 * group's (`draft`, kept by the window: the box is marked, a bar shows the price it gives, Save or
 * Cancel, and closing asks first). Each time `reveal` goes up, the draft is scrolled into view.
 */
export function PricingTab(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        draft: ItemDraft | null;
        onDraft: (draft: ItemDraft | null) => void;
        reveal: number;
        /** Open its Costing tab (where the cost is set). */
        onCostTab: () => void;
        /** Open its Contents tab (built from products: where its price is set). */
        onPartsTab: () => void;
        /** Its price warnings in full, attached to what prices it: the group's card, or its own price settings. */
        fix?: ReactNode;
        /** Bumped by a warning's Show: the price warnings (`fix`), or what it makes, scrolled to and flashed. */
        fixToken?: number;
        profitToken?: number;
        /** Its cost being changed (its Costing tab's draft): what it makes is shown with it. */
        costDraft: CostDraft | null;
    }>,
) {
    const { data, item } = props;
    const canEdit = can(data, "items.pricing");
    const pricing = data.itemPricing[item.uuid];
    const box = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // Once per ask (lib/flash.ts): back on this tab, it isn't scrolled to again.
        if (!props.reveal || alreadyShown(props.reveal)) return;
        box.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        markShown(props.reveal);
    }, [props.reveal]);
    // The group it's in, if any (pricing it, or overriding it).
    const group = memberOf(data, item.uuid);
    // Overriding its group's price (or starting to: its Override pressed): its own pricing sits in the group's blue panel (its save bar with it).
    const overridesGroup = group !== undefined && (pricing?.overridesGroup === true || props.draft?.override === true);

    // The pricing as edited (the draft), else as saved. A change back to what's saved is no change.
    const saved = savedItemDraft(data, item);
    const d = props.draft ?? saved;
    const edit = (next: ItemDraft) => {
        // Starting an override, it stays a draft (to save or cancel) even with nothing changed yet.
        props.onDraft(sameItemDraft(next, saved, data.taxRates) && !next.override ? null : next);
        return Promise.resolve();
    };
    const taxNow = quoteRate(data, d.tax);
    const basis: Basis = { includesTax: d.tax.includesTax, tax: taxNow.tax };
    const now = itemAsDrafted(data, item, d);
    const priceNow = draftPrice(data, item, d);

    /** The rule as edited, or a plain one standing for the typed price (so extras can be added to it). */
    const current: PricingRule = d.rule ?? typedPriceRule(d.price, basis);
    /** A change to its rule: back to a typed price when nothing's left to work out. */
    const saveRule = (patch: Partial<PricingRule>) => {
        const next = { ...current, ...patch };
        // Nothing to keep: a typed price.
        return edit(isPlain(next) ? { ...d, rule: null, price: shownAmount(next.basePrice, basis) } : { ...d, rule: next });
    };
    /** Another tax: typed amounts stay as typed, read the new way (pricing.ts keepShownAmounts). */
    const changeTax = (tax: TaxQuote) => {
        const to = quoteRate(data, tax);
        const toBasis = { includesTax: tax.includesTax, tax: to.tax };
        return edit({ rule: d.rule ? keepShownAmounts(d.rule, basis, toBasis) : null, price: d.price, tax });
    };

    // Worked out from the cost: not without one (pricing.ts missingCost).
    const noCost = toNumber(now.item.cost) <= 0 ? "Markup needs a cost: set one on the Costing tab." : undefined;

    // Switching keeps the price it gives: a markup's % worked out exactly (only rounded as it's shown), a fixed price as it is.
    const switchSource = (to: PriceMode) => {
        const { costNet, markupNet } = exactMarkup(now.item, now.tax, d.rule ?? undefined);
        if (to === "percent") return saveRule({ mode: "percent", markupValue: percentFor(markupNet, costNet) });
        // Fixed price: keep the price it gives now.
        return saveRule({ mode: "manual", basePrice: baseForPrice(now.item, now.tax, priceNow), markupValue: "0" });
    };

    // Labour time (LabourTimeCard.tsx): its price is a share of an hourly labour product's, set straight away (no draft) on the first
    // rate, at the time its price comes to on it. Leaving it: Manual keeps its price; Markup works one out, as switching does.
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const labour = group ? undefined : pricing?.labour;
    const startLabour = async () => {
        const start = startingLabour(data, item);
        if (!start) return;
        // Its pricing being changed goes (labour time is saved straight away): asked first.
        if (props.draft && !(await confirm({ title: "Discard your pricing changes?", message: "Labour time is saved straight away, in place of the changes you haven't saved.", confirmLabel: "Discard and use labour time", tone: "danger" }))) return;
        props.onDraft(null);
        await saver.setLabourTime(item.uuid, start);
    };
    const leaveLabour = (to: "manual" | "percent") => saver.setLabourTime(item.uuid, null).then(() => (to === "percent" ? switchSource("percent") : undefined));
    // Built from products (packages.ts): its price is what they add up to, changed on its Contents tab.
    const fromParts = !group && pricing?.fromParts === true;
    // A plain item (in ServiceM8, not built): its price typed, nothing worked out (that's for products: catalogue.ts).
    const plain = !isProduct(item) && !fromParts;

    // Its own pricing, edited as a draft in the editor groups use too (PricingEditor.tsx).
    const ruleFields = (
        <PricingEditor
            data={data}
            rule={current}
            tax={d.tax}
            canEdit={canEdit}
            price={priceNow}
            costBasedUnavailable={noCost}
            // What a % markup is on: its cost before tax, taxed as the draft is.
            costNet={toNet(toNumber(now.item.cost), now.item.price_includes_taxes, now.tax)}
            estimatedCostNet={itemProfit(data, item, itemTax(data, item).tax).costNet}
            onCostTab={props.onCostTab}
            onLabour={plain ? undefined : startLabour}
            labourUnavailable={labourUnavailable(data, item)}
            pricedBy={plain ? "A typed price." : undefined}
            editing={props.draft !== null}
            tinted={overridesGroup}
            // Being edited: its save bar is where its price warning's Show brings you (FixHere.tsx: the standard).
            footer={
                props.draft ? (
                    <FixHere attached token={props.fixToken} className="overflow-visible rounded-none">
                        <ItemSave data={data} item={item} draft={props.draft} tinted={overridesGroup} onDone={() => props.onDraft(null)} />
                    </FixHere>
                ) : null
            }
            boxRef={box}
            onRule={saveRule}
            onMode={switchSource}
            onTax={changeTax}
        />
    );

    // Priced by its group (not overriding it): the group's pricing in the same fields, greyed (changed on the group, or by
    // overriding it here: Override starts from these), so how its price is made is read the same as an item's own.
    const groupFields = (() => {
        if (!group || overridesGroup || !pricing) return null;
        const g: ItemDraft = {
            rule: ruleOnly(pricing),
            price: toCents(toNumber(item.price)),
            tax: pricing.groupTax ? { rateUuid: pricing.groupTax.rateUuid, includesTax: pricing.groupTax.includesTax } : saved.tax,
        };
        const gNow = itemAsDrafted(data, item, g);
        const none = () => Promise.resolve();
        return (
            <PricingEditor
                data={data}
                rule={ruleOnly(pricing)}
                tax={g.tax}
                canEdit={false}
                price={draftPrice(data, item, g)}
                costNet={toNet(toNumber(gNow.item.cost), gNow.item.price_includes_taxes, gNow.tax)}
                estimatedCostNet={itemProfit(data, item, itemTax(data, item).tax).costNet}
                onCostTab={props.onCostTab}
                editing={false}
                onRule={none}
                onMode={none}
                onTax={none}
            />
        );
    })();

    // ServiceM8 prices it itself (its pricing strategy, servicem8Pricing.ts): what that comes to, read-only, until it's priced here again.
    const inServiceM8 = data.pricedInServiceM8.includes(item.uuid) && !props.draft;

    let pricedBy = ruleFields;
    if (fromParts) pricedBy = <PartsPriceCard data={data} item={item} onCostTab={props.onCostTab} onParts={props.onPartsTab} />;
    else if (labour) pricedBy = <LabourTimeCard data={data} item={item} onLeave={leaveLabour} onCostTab={props.onCostTab} />;

    // Its tab's height (RecordWindow), so what it makes per unit (ProfitBox) sits at the bottom of the window when there's room.
    return (
        <div className="flex flex-1 flex-col">
            <section>
                {inServiceM8 ? (
                    <section aria-label="How the price is worked out" className="box">
                        <PricingCard label="Price" aside={SELLS_FOR} header={<PricedInServiceM8 data={data} item={item} canEdit={canEdit} />}>
                            <PricingEditor
                                data={data}
                                rule={servicem8Rule(data, item)}
                                tax={saved.tax}
                                canEdit={false}
                                price={toCents(toNumber(item.price))}
                                pricedBy="Priced by a ServiceM8 pricing strategy, from its cost."
                                costNet={toNet(toNumber(item.cost), item.price_includes_taxes, itemTax(data, item).tax)}
                                onCostTab={props.onCostTab}
                                editing={false}
                                onRule={() => Promise.resolve()}
                                onMode={() => Promise.resolve()}
                                onTax={() => Promise.resolve()}
                            />
                        </PricingCard>
                    </section>
                ) : (
                <PricingBox
                    data={data}
                    item={item}
                    group={group}
                    overriding={overridesGroup}
                    // Override: its own pricing, starting from the group's (the same price), as a draft to save or cancel.
                    onOverride={pricing ? () => props.onDraft({ ...saved, rule: ruleOnly(pricing), override: true }) : undefined}
                    onLeave={() => props.onDraft(null)}
                    // Which group prices it (or none), first in the box; picking one drops its own pricing's draft. Only a
                    // product can use one: an item is plain, or its products price it.
                    picker={isProduct(item) ? <GroupField data={data} item={item} onChange={() => props.onDraft(null)} /> : null}
                    fix={props.fix}
                    fixToken={props.fixToken}
                    groupFields={groupFields}
                >
                    {pricedBy}
                </PricingBox>
                )}
            </section>
            {/* An hourly labour rate: the products that are labour time on it, to update together when it changes. */}
            <LabourSharesCard data={data} rate={item} />

            {/* At the bottom (of the window when there's room): what it makes per unit, its drafts (this one, its cost's) as they'd make it. */}
            <ProfitBox data={data} item={item} draft={props.draft} costDraft={props.costDraft} fixToken={props.profitToken} />
        </div>
    );
}

/**
 * Which pricing group prices it (shared with the other items using it), at the top of its Pricing tab: none (its own pricing),
 * or one, with Open group beside it. Not using one, New group makes one from its price (a fixed price, to change in its window).
 * Changing it drops its own pricing's draft.
 */
function GroupField({ data, item, onChange }: Readonly<{ data: PriceListData; item: MaterialRow; onChange: () => void }>) {
    const saver = usePriceListSaver();
    const windows = usePriceListWindows();
    const group = memberOf(data, item.uuid);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const pick = (v: string) => {
        if (v === (group?.id ?? "")) return Promise.resolve();
        onChange();
        return saver.useGroup(item.uuid, v || null);
    };
    // A fixed price made from its price (as it is now), with it using it, named after it for now: its window opens to rename it.
    const newGroup = () => {
        const { basePrice } = typedPriceRule(item.price, { includesTax: item.price_includes_taxes, tax: itemTax(data, item).tax });
        onChange();
        setCreating(true);
        setError(null);
        saver
            .createGroup({ name: freeGroupName(data, splitName(item.name, data.nameTags).base), basePrice, materialUuid: item.uuid })
            .then((s) => windows.openGroup(s.id, { rename: true }))
            .catch((err: Error) => setError(err))
            .finally(() => setCreating(false));
    };
    return (
        <div className="space-y-1">
            <SectionHeading label="Group" />
            <div className="flex items-center gap-2">
                <SelectField
                    hideLabel
                    label="Group"
                    value={group?.id ?? ""}
                    disabled={!can(data, "groups")}
                    options={[
                        { value: "", label: "None" },
                        ...data.groups.map((s) => ({ value: s.id, label: `${s.name} · ${GROUP_KIND_LABEL[groupKind(s)]} (${plural(membersOf(data, s.id).length, "product")})` })),
                    ]}
                    onSave={pick}
                    className="min-w-0 flex-1"
                />
                {/* Using one: its window (its pricing and all its items). Not: a group from its price, to share with other items. */}
                {group ? (
                    <button type="button" className="btn-secondary shrink-0" title="Its window: its pricing and all its items" onClick={() => windows.openGroup(group.id)}>
                        Open group
                    </button>
                ) : can(data, "groups") ? (
                    <button type="button" className="btn-secondary shrink-0" title="A group from its price, to share with other items (it uses it): its kind is chosen in its window" disabled={creating} onClick={newGroup}>
                        <Plus className="size-4" /> New group
                    </button>
                ) : null}
            </div>
            <ErrorNotice error={error} />
        </div>
    );
}

/**
 * What a ServiceM8 pricing strategy comes to on an item, as a markup: its price over its ServiceM8 cost (what the strategy works
 * from). Nothing else is known of it (ServiceM8's API doesn't show its strategies).
 */
function servicem8Rule(data: PriceListData, item: MaterialRow): PricingRule {
    const tax = itemTax(data, item).tax;
    const costNet = toNet(toNumber(item.cost), item.price_includes_taxes, tax);
    const priceNet = toNet(toNumber(item.price), item.price_includes_taxes, tax);
    return { mode: "percent", markupValue: percentFor(priceNet - costNet, costNet), basePrice: "0" };
}

/**
 * Along the top of its Pricing card: ServiceM8 prices it, and how to price it here instead. Built from products: what they add up to
 * beside what ServiceM8 charges, warned about (attention.ts); pricing it here again saves theirs, and keeping ServiceM8's makes it a
 * plain item (asked first).
 */
function PricedInServiceM8(props: Readonly<{ data: PriceListData; item: MaterialRow; canEdit: boolean }>) {
    const { data, item } = props;
    const saver = usePriceListSaver();
    const fmt = useMoney();
    const makePlain = useMakePlain();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const pricing = data.itemPricing[item.uuid];
    const built = pricing?.fromParts === true;
    const products = fmt.money(fromNet(toNumber(pricing?.basePrice ?? "0"), item.price_includes_taxes, itemTax(data, item).tax));
    const run = (fn: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        fn()
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    // Priced here again; built from products, at what they add up to now (still priced by ServiceM8: that's said, and it's marked again).
    const priceHere = () => run(() => saver.priceHere(item.uuid).then(() => (built ? saver.updateToPricing(item.uuid) : undefined)));
    // Short: what it charges, and the one step in ServiceM8 before pricing it here (its buttons say the rest).
    const says = built
        ? `It charges ${fmt.money(item.price)}; its products add up to ${products}. To use theirs, set its Pricing Strategy to Manual in ServiceM8 first.`
        : "A price set here isn't kept. To price it here, set its Pricing Strategy to Manual in ServiceM8 first.";
    return (
        <>
            <Notice
                tone={built ? "warning" : undefined}
                title="Priced by a ServiceM8 strategy."
                actions={
                    props.canEdit ? (
                        <>
                            {/* Built, its price is sent to ServiceM8 too: held, as every such write. */}
                            {built ? (
                                <HoldButton
                                    className="btn-secondary px-2.5 py-1 text-xs"
                                    label="Use products' price"
                                    disabled={busy}
                                    title={`Once its Pricing Strategy is Manual in ServiceM8: its price goes to ${products}, what its products add up to`}
                                    onDone={priceHere}
                                />
                            ) : (
                                <button type="button" className="btn-secondary px-2.5 py-1 text-xs" disabled={busy} title="ServiceM8 no longer prices it: its Pricing Strategy is Manual there" onClick={priceHere}>
                                    Price it here
                                </button>
                            )}
                            {built ? (
                                <button
                                    type="button"
                                    className="btn-secondary px-2.5 py-1 text-xs"
                                    disabled={busy}
                                    title="Unlinks it from the Catalogue: it keeps ServiceM8's price, typed in (asked first)"
                                    onClick={() => run(() => makePlain(item, { what: "price", amount: item.price }))}
                                >
                                    Keep {fmt.money(item.price)}
                                </button>
                            ) : null}
                        </>
                    ) : undefined
                }
            >
                {says}
            </Notice>
            <ErrorNotice error={error} />
        </>
    );
}

/**
 * Saving an item's pricing draft: the price it gives (now -> new, with the tax when that changes),
 * then save (with a rule: updating the price, or leaving it to update) or cancel. A product's price is always what its pricing
 * gives (productsFollow.ts): just Save.
 */
function ItemSave(props: Readonly<{ data: PriceListData; item: MaterialRow; draft: ItemDraft; /** On the overriding panel: a blue line above it. */ tinted?: boolean; onDone: () => void }>) {
    const { data, item, draft } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const saved = savedItemDraft(data, item);
    const taxChanged = quoteKey(draft.tax, data.taxRates) !== quoteKey(saved.tax, data.taxRates);
    const next = draftPrice(data, item, draft);
    const priceMoves = !samePrice(next, item.price);
    const moves = taxChanged || priceMoves;
    const labelNow = basisLabel(item.price_includes_taxes, itemTax(data, item).rate);
    const labelNext = basisLabel(draft.tax.includesTax, quoteRate(data, draft.tax).rate);
    // Something typed above can't be taken (the draft has what it had before): not saved until it's put right.
    const typedBad = useDraftInvalid();

    const save = async (updatePrice: boolean) => {
        // A typed price left for later is saved as a fixed price (a rule), so the item shows the new one as a price to update.
        const basis = { includesTax: draft.tax.includesTax, tax: quoteRate(data, draft.tax).tax };
        const later = typedPriceRule(draft.price, basis);
        // Overriding its group, it keeps a rule (a set price): a typed price would take it out of the group.
        const rule = draft.rule ?? (draft.override || (priceMoves && !updatePrice) ? later : null);
        setBusy(true);
        setError(null);
        try {
            await saver.saveItemPricing(item.uuid, {
                rule,
                ...(taxChanged ? { tax: draft.tax } : {}),
                ...(rule === null ? { price: draft.price } : {}),
                updatePrice,
                ...(draft.override ? { overrideGroup: true } : {}),
            });
            props.onDone();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };

    return (
        // Like a warning strip (FixHere attached): along the bottom of the editor, edge to edge, the same size; what saving does on
        // the left (wrapping as it needs), its buttons on the right (under it only when narrow).
        <div className={clsx("-mx-4 -mb-4 border-t px-3 py-2", props.tinted ? "border-override/30" : "border-brand-500/30")}>
            <DraftBar
                message={
                    <>
                        {/* What it changes (as its question says it, if it asks). */}
                        {moves ? null : "The price doesn't change."}
                        <ChangeLines price={moves ? { from: item.price, to: next, ...(taxChanged ? { fromLabel: labelNow, toLabel: labelNext } : {}) } : undefined} />
                        {typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                    </>
                }
                busy={busy}
                error={error}
                actions={
                    <>
                        {priceMoves && isProduct(item) ? (
                            <button type="button" className="btn-primary" disabled={busy || typedBad} title={`Save the pricing: its price goes to ${fmt.money(next)}`} onClick={() => void save(true)}>
                                Save pricing
                            </button>
                        ) : priceMoves ? (
                            // Update now, or leave the price to update: always in that order.
                            <div className="flex flex-wrap items-center gap-1.5">
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={busy || typedBad}
                                    title={`Save the pricing and change the price to ${fmt.money(next)}`}
                                    onClick={() => void save(true)}
                                >
                                    Save &amp; update
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    disabled={busy || typedBad}
                                    title={`Save the pricing only: the price stays ${fmt.money(item.price)}, and it shows as to update until updated`}
                                    onClick={() => void save(false)}
                                >
                                    Save only
                                </button>
                            </div>
                        ) : (
                            <button type="button" className="btn-primary" disabled={busy || typedBad} onClick={() => void save(false)}>
                                Save pricing
                            </button>
                        )}
                        <button type="button" className="btn-secondary" disabled={busy} onClick={props.onDone}>
                            Cancel
                        </button>
                    </>
                }
            />
        </div>
    );
}

/**
 * How the price is worked out, in one box: which group prices it (GroupField), then its pricing card: the group's
 * (shared, so only shown here: it's edited in the group's window, where all its items are), or its own fields. Outside the box,
 * the item's own things (cost, what it makes).
 */
function PricingBox(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        group: GroupRow | undefined;
        /** Overriding its group's price, or starting to (a draft): its own pricing shown, under the group's card. */
        overriding: boolean;
        /** Start overriding its group's price (a draft); none: it can't (nothing to start from). */
        onOverride?: () => void;
        /** Back to its group's price: its own pricing's draft (if any) goes. */
        onLeave: () => void;
        /** Which group prices it, above its card, in the same box. */
        picker: ReactNode;
        /** Its price warnings, attached to what prices it (FixHere), and Show's token. */
        fix?: ReactNode;
        fixToken?: number;
        /** Priced by its group: the group's pricing, read-only, in the group's card in place of its own fields. */
        groupFields?: ReactNode;
        children: ReactNode;
    }>,
) {
    const { data, group } = props;
    const fix = props.fix ? (
        <FixHere attached token={props.fixToken}>
            {props.fix}
        </FixHere>
    ) : undefined;
    return (
        <section aria-label="How the price is worked out" className="box">
            {props.picker}
            {group ? (
                // Overriding: its own pricing sits inside the group's card, under it, as one blue panel.
                <GroupCard data={data} item={props.item} group={group} overriding={props.overriding} onOverride={props.onOverride} onLeave={props.onLeave} fix={fix}>
                    {props.overriding ? props.children : props.groupFields}
                </GroupCard>
            ) : (
                // Priced its own way: the same card, its warning along its bottom.
                <PricingCard label="Price" aside={SELLS_FOR} fix={fix}>{props.children}</PricingCard>
            )}
        </section>
    );
}

/**
 * Pricing's card, the same wherever pricing is set (an item's, a group's), as Name tags' box is: its `label` above it ("Price" on an item, "Pricing" on a group; with
 * `aside` on the right), any `header` along its top (an item's group), the editor in it (`children`, padded so its editing tint
 * and save bar reach its edges), and along its bottom its warnings (`fix`).
 */
export function PricingCard(props: Readonly<{ label?: string; aside?: ReactNode; header?: ReactNode; overriding?: boolean; fix?: ReactNode; children?: ReactNode }>) {
    // Its body padded as the editor's editing tint and save bar expect (PricingEditor.tsx), so they reach its edges. Overriding its
    // group's price: its border and body blue (its header says so too).
    return (
        <SectionCard label={props.label ?? "Pricing"} aside={props.aside} header={props.header} tone={props.overriding ? "override" : undefined} bodyClassName="px-4 pt-3.5 pb-4" fix={props.fix}>
            {props.children}
        </SectionCard>
    );
}

/**
 * In a group: its pricing card, headed by the group (GroupCardHeader): what the group sets, and Override, which opens this
 * product's own pricing under it as a draft (starting from the group's: saving makes it an override, Cancel doesn't), the card blue;
 * overriding, Use group's price (asked first: its own pricing goes) instead.
 */
function GroupCard(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        group: GroupRow;
        /** Overriding its price, or starting to (a draft, not saved yet). */
        overriding: boolean;
        /** Start overriding (a draft); none: it can't. */
        onOverride?: () => void;
        /** Back to the group's price: its own pricing's draft (if any) goes. */
        onLeave: () => void;
        /** The group's pricing, read-only; overriding, its own pricing: under its header (one panel). */
        children?: ReactNode;
        /** Its price warnings (FixHere attached), along the card's bottom. */
        fix?: ReactNode;
    }>,
) {
    const { data, item, group, overriding } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const taxed = groupTaxBasis(data, group.tax);
    // Overriding as saved (not just started: a draft, cancelled with its save bar).
    const saved = Boolean(data.itemPricing[item.uuid]?.overridesGroup);
    const canEdit = can(data, "items.pricing");
    // Back to the group's price: asked first (its own pricing goes).
    const useGroups = async () => {
        const yes = await confirm({
            title: `Use the ${group.name} group's price?`,
            subject: itemName(data, item),
            message: "Its own pricing goes, and it's priced and taxed as the group says. If that gives another price, it shows as a price to update.",
            confirmLabel: "Use the group's price",
            tone: "primary",
        });
        if (!yes) return;
        props.onLeave();
        setError(null);
        setBusy(true);
        saver
            .setOverride(item.uuid, false)
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    // Its header along the top of the pricing card (overriding: its own pricing under it, the card blue).
    const header = (
        // Over something (its own pricing overriding it, blue; or the group's, read-only): a line under it.
        <div className={clsx("space-y-2 px-3 py-2.5", overriding ? "border-b border-override/30 bg-override-header" : props.children ? "border-b border-line bg-surface-2" : undefined)}>
            <GroupCardHeader
                group={group}
                badge={saved ? { text: "Price overridden", title: "This product is priced its own way (below), not by the group: changes to the group's pricing don't reach it" } : undefined}
                summary={describeGroup(group, taxed, basisLabel(taxed.includesTax, taxed.rate), fmt)}
                actions={
                    // Override (its own pricing, a draft: its save bar saves or cancels it), or overriding, back to the group's.
                    <>
                        {canEdit && !overriding && props.onOverride ? (
                            <button type="button" className="btn-secondary" title="Price it its own way, starting from the group's pricing: save to override, or cancel" onClick={props.onOverride}>
                                Override
                            </button>
                        ) : null}
                        {canEdit && saved ? (
                            <button type="button" className="btn-secondary" disabled={busy} title="Its own pricing goes: priced by the group again (asked first)" onClick={() => void useGroups()}>
                                Use group&apos;s price
                            </button>
                        ) : null}
                    </>
                }
            />
            <ErrorNotice error={error} />
        </div>
    );
    return (
        <PricingCard label="Price" aside={SELLS_FOR} header={header} overriding={overriding} fix={props.fix}>
            {props.children}
        </PricingCard>
    );
}

/**
 * Its pricing gives a different price than it has: update it (an item's, saved to ServiceM8: held), or (a product) keep the one it
 * has (in a group: overriding the group's). An item linked to products keeps no other price here: Unlink, its More menu, is for
 * that. Along the bottom of what prices it (PricingBox).
 */
export function CheckNotice({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const check = needsCheck(item, itemTax(data, item), data.itemPricing[item.uuid]);
    if (!check) return null;
    const byGroup = groupOf(data, item.uuid);
    // Built from products: its price is theirs. Its price only (its cost to update is its Costing tab's).
    const fromParts = !byGroup && data.itemPricing[item.uuid]?.fromParts === true;
    // A labour time's rate (LabourTimeCard.tsx): what sets its price.
    const labourRate = byGroup ? undefined : data.materials.find((m) => m.uuid === data.itemPricing[item.uuid]?.labour?.rateUuid);
    let why = "Set by its pricing.";
    if (byGroup) why = `Set by ${byGroup.name}.`;
    else if (labourRate) why = `Set by ${itemName(data, labourRate)}.`;
    else if (fromParts) why = "Its products changed: what they add up to now.";
    if (check.reason === "edited") why = "Changed in ServiceM8.";
    // Its group taxes it otherwise: the new price is in that basis, and updating sets it.
    // Its group taxes it otherwise: both prices say how they're taxed (the new one in that basis: updating sets it), so the change
    // reads as what it is, not a jump.
    const now = check.taxTo ? `${fmt.money(item.price)} ${basisLabel(item.price_includes_taxes, itemTax(data, item).rate)}` : fmt.money(item.price);
    const target = check.taxTo ? `${fmt.money(check.target)} ${basisLabel(check.taxTo.includesTax, groupTaxBasis(data, check.taxTo).rate)}` : fmt.money(check.target);
    const run = (fn: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        fn()
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    const keep = () => run(() => saver.keepPrice(item.uuid));
    const update = () => run(() => saver.updateToPricing(item.uuid));
    const updateLabel = `Update to ${fmt.money(check.target)}`;
    // An item's is saved to ServiceM8: held, so a reflex click can't. A product's is only kept here.
    const product = isProduct(item);
    return (
        <Notice
            tone="warning"
            title="Price to update:"
            actions={
                can(data, "items.pricing") ? (
                    <>
                        {product ? (
                            <button type="button" className="btn-primary" disabled={busy} onClick={update}>
                                {updateLabel}
                            </button>
                        ) : (
                            <HoldButton className="btn-primary" label={updateLabel} disabled={busy} title="Saved to ServiceM8" onDone={update} />
                        )}
                        {/* A product: keeping its price means pricing it its own way (in a group: overriding the group's, staying in it). */}
                        {product ? (
                            <button
                                type="button"
                                className="btn-secondary"
                                disabled={busy}
                                title={byGroup ? `Keep this price as its own, overriding the ${byGroup.name} group's: it stays in the group` : undefined}
                                onClick={keep}
                            >
                                Keep {fmt.money(item.price)}
                                {byGroup ? " (override)" : ""}
                            </button>
                        ) : null}
                    </>
                ) : null
            }
        >
            {/* The change on its title's line (now -> new, as its save bar says it), why on the next. */}
            <span className="tabular-nums">
                {now} → <strong>{target}</strong>
            </span>
            <span className="block">{why}</span>
            <ErrorNotice error={error} />
        </Notice>
    );
}

/** "Making a loss", "Making nothing" or "Making too little" (on its cost): its pill in Needs attention (its figures on the Pricing tab say how much). */
export function profitTitle(data: PriceListData, item: MaterialRow) {
    const net = itemProfit(data, item, itemTax(data, item).tax).profitNet ?? 0;
    if (net < 0) return "Making a loss";
    return net === 0 ? "Making nothing" : "Making too little";
}
